import { useEffect, useId, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, ChevronDown, ChevronUp, Construction, Languages, LogOut, MessageSquareWarning } from "lucide-react";
import { previewFeatureFor } from "@hisense/shared";
import { navFor } from "@/app/nav";
import { useAuth } from "@/app/auth";
import { cn } from "@/lib/cn";
import { LanguageSwitch } from "./LanguageSwitch";
import { ReleaseBadge } from "@/components/releases/ReleaseBadge";
import { ChangePasswordModal } from "@/components/shell/ChangePasswordModal";
import { matchNavItem } from "./breadcrumb";
import { ButtonRing } from "@/components/brand/ButtonRing";
import { WALKTHROUGH, showWalkthrough } from "@/app/walkthrough";

const HISENSE_LOGO_SRC = "/brand/hisense-logo.webp";
const HISENSE_MARK_SRC = "/brand/hisense-mark.webp";

const ROLE_TONE: Record<string, string> = {
  admin: "var(--color-brand)",
  manager: "var(--color-info)",
  accountant: "var(--color-brass)",
  viewer: "var(--color-ink-faint)",
};

interface SidebarProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onNavigate }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const groupId = useId();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const activeGroup = matchNavItem(pathname)?.groupKey;

  // Navigation from search, a document link or browser history reveals its group.
  useEffect(() => {
    if (activeGroup) {
      setExpandedGroups((previous) => ({ ...previous, [activeGroup]: true }));
    }
  }, [pathname, activeGroup]);

  const displayName = user?.displayName ?? "—";
  const role = user?.role ?? "viewer";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");

  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    onNavigate?.();
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      {changingPassword && (
        <ChangePasswordModal onClose={() => setChangingPassword(false)} />
      )}
      <nav
        className={cn(
          "flex h-full flex-col bg-sunken/60",
          collapsed ? "w-[68px]" : "w-[248px]",
        )}
        aria-label={t("common.primaryNavigation")}
      >
        <div
          className={cn(
            "flex h-14 shrink-0 items-center border-b border-line",
            collapsed ? "justify-center px-0" : "px-4",
          )}
        >
          <img
            src={collapsed ? HISENSE_MARK_SRC : HISENSE_LOGO_SRC}
            alt={t("login.brandLogoAlt")}
            width={collapsed ? 300 : 640}
            height={167}
            className={cn(
              "h-auto object-contain",
              collapsed ? "w-10" : "w-32 max-w-full",
            )}
          />
        </div>

        <div className="flex-1 overflow-y-auto px-2.5 py-3">
          {navFor(role, user?.portalSupplierId, user?.access).map((group) => {
            const expanded = expandedGroups[group.key] ?? !group.items.every((item) => item.comingSoon);
            const listId = `${groupId}-${group.key}`;
            return (
            <div key={group.key} className="mb-4 last:mb-1">
              {!collapsed && (
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={listId}
                  onClick={() => setExpandedGroups((previous) => ({ ...previous, [group.key]: !expanded }))}
                  className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-2xs font-semibold uppercase tracking-wide text-ink-soft hover:bg-surface/70"
                >
                  <span>{t(`nav.${group.key}`)}</span>
                  <ChevronDown aria-hidden="true" className={cn("size-3.5 shrink-0 transition-transform", !expanded && "-rotate-90")} />
                </button>
              )}
              <ul id={listId} hidden={!collapsed && !expanded} className={cn("flex-col gap-0.5", collapsed || expanded ? "flex" : "hidden")}>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const pendingReview = !!item.reviewRuleIds?.length;
                  // Tính năng mới đang làm (30/10/2026): cùng danh sách với cổng mật khẩu, nên có dấu là có mật khẩu.
                  const inProgress = !!previewFeatureFor(item.to, "page");
                  // Dấu nút áo: mục đã rà cùng chủ dự án (chỉ localhost, xem app/walkthrough.ts).
                  const walked = showWalkthrough ? WALKTHROUGH[item.key] : undefined;
                  const walkedLabel = walked ? `${t("nav.walkthroughDone", { date: walked.date })}${walked.open ? ` · ${walked.open}` : ""}` : "";
                  const label = `${t(`nav.${item.key}`)}${item.comingSoon ? ` · ${t("nav.inDevelopment")}` : ""}${inProgress ? ` · ${t("nav.inProgress")}` : ""}${pendingReview ? ` · ${t("nav.pendingReview")}` : ""}${walked ? ` · ${walkedLabel}` : ""}`;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        onClick={onNavigate}
                        title={collapsed || pendingReview || inProgress || walked ? label : undefined}
                        aria-label={label}
                        className={({ isActive }) =>
                          cn(
                            "group relative flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm transition-colors",
                            collapsed && "justify-center",
                            isActive
                              ? "bg-surface font-medium text-brand shadow-[var(--shadow-card)]"
                              : "text-ink-soft hover:bg-surface/70 hover:text-ink",
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && !collapsed && (
                              <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-brand" />
                            )}
                            <Icon
                              className={cn(
                                "size-[17px] shrink-0",
                                isActive
                                  ? "text-brand"
                                  : "text-ink-faint group-hover:text-ink-soft",
                              )}
                              strokeWidth={2}
                            />
                            {walked && (
                              <span aria-hidden="true" className={cn("grid shrink-0 place-items-center", collapsed ? "absolute -bottom-0.5 -right-0.5" : "order-last")}>
                                <ButtonRing size={collapsed ? 11 : 17} tone={walked.open ? "warn" : "ok"} holes={4} />
                              </span>
                            )}
                            {inProgress && (
                              <span aria-hidden="true" className={cn(
                                "grid size-5 shrink-0 place-items-center rounded-sm border border-info/30 bg-info-tint text-info",
                                collapsed ? "absolute -right-0.5 -top-0.5 size-3.5" : "order-last",
                              )}>
                                <Construction className={collapsed ? "size-2.5" : "size-3.5"} />
                              </span>
                            )}
                            {pendingReview && (
                              <span aria-hidden="true" className={cn(
                                "grid size-5 shrink-0 place-items-center rounded-sm border border-warn/30 bg-warn-tint text-warn",
                                collapsed ? "absolute -right-0.5 -top-0.5 size-3.5" : "order-last",
                              )}>
                                <MessageSquareWarning className={collapsed ? "size-2.5" : "size-3.5"} />
                              </span>
                            )}
                            {!collapsed && (
                              <span className="min-w-0 flex-1">
                                <span className="block leading-snug">{t(`nav.${item.key}`)}</span>
                                {item.comingSoon && <span className="block text-2xs font-normal text-ink-faint">{t("nav.inDevelopment")}</span>}
                                {inProgress && <span className="block text-2xs font-normal text-info">{t("nav.inProgress")}</span>}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );})}
        </div>

        <div className="relative shrink-0 border-t border-line p-2">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setMenuOpen(false);
            }}
            className={cn(
              "group flex w-full items-center gap-2.5 rounded-sm p-1.5 text-left transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30",
              collapsed && "justify-center",
              menuOpen && "relative z-40 bg-surface",
            )}
            title={collapsed ? displayName : undefined}
            aria-label={displayName}
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
          >
            <span
              className="grid size-8 shrink-0 place-items-center rounded-sm text-xs font-semibold text-white"
              style={{ background: ROLE_TONE[role] ?? "var(--color-brand)" }}
            >
              {initials}
            </span>
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-xs font-medium text-ink">
                    {displayName}
                  </span>
                  <span className="flex min-w-0 items-center gap-1 text-[10px] uppercase tracking-[0.06em] text-ink-faint">
                    <span className="truncate">{user?.access?.mode === "custom" && role !== "admin" ? t("account.customAccess") : t(`roles.${role}`)}</span>
                  </span>
                </span>
                <ChevronUp
                  className={cn(
                    "size-4 shrink-0 text-ink-faint transition-transform group-hover:text-ink-soft",
                    menuOpen && "rotate-180",
                  )}
                />
              </>
            )}
          </button>

          <ReleaseBadge collapsed={collapsed} onNavigate={onNavigate} />

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
              />
              <div
                role="dialog"
                aria-label={displayName}
                className={cn(
                  "absolute z-40 overflow-hidden rounded-md border border-line bg-surface py-1 shadow-[var(--shadow-pop)]",
                  collapsed
                    ? "bottom-0 left-[calc(100%+6px)] w-52"
                    : "bottom-[calc(100%+6px)] left-2 right-2",
                )}
              >
                <div className="border-b border-line px-3 py-2">
                  <p className="truncate text-sm font-medium text-ink">
                    {displayName}
                  </p>
                  <p className="truncate text-xs text-ink-faint">
                    {user?.email}
                  </p>
                </div>
                <div className="border-b border-line px-3 py-2.5">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium text-ink-soft">
                    <Languages className="size-4 text-ink-faint" />
                    <span>{t("common.language")}</span>
                  </div>
                  <LanguageSwitch />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setChangingPassword(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-soft transition-colors hover:bg-sunken hover:text-ink focus-visible:bg-sunken focus-visible:outline-none"
                >
                  <KeyRound className="size-4 text-ink-faint" />
                  {t("account.changePassword")}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-soft transition-colors hover:bg-sunken hover:text-ink focus-visible:bg-sunken focus-visible:outline-none"
                >
                  <LogOut className="size-4 text-ink-faint" />
                  {t("common.signOut")}
                </button>
              </div>
            </>
          )}
        </div>
      </nav>
    </>
  );
}
